import React from 'react';
import { FOCUS_RING, cx } from './toneStyles';

/**
 * Button — every text button in the app.
 *
 * Props
 *   variant   'primary' | 'secondary' | 'ghost' | 'danger' | 'link' | 'soft'
 *   size      'sm' (36px) | 'md' (44px, default) | 'lg' (52px)
 *   icon      Md icon component rendered before the label (aria-hidden)
 *   iconRight Md icon component rendered after the label
 *   loading   shows a spinner, keeps the width, sets aria-busy + disabled
 *   fullWidth w-full
 *
 * Native `type` is passed through untouched (a Button inside a <form> submits, like
 * before) — pass type="button" explicitly for non-submitting buttons in forms.
 * `size="sm"` is for secondary/inline actions only; never the primary action of a flow.
 */
const VARIANTS = {
  primary: 'btn-primary',
  secondary: 'btn-secondary',
  danger: 'btn-danger',
  ghost: cx(
    'inline-flex items-center justify-center gap-2 rounded-lg font-medium',
    'bg-transparent text-primary-700 dark:text-primary-300',
    'hover:bg-primary-50 active:bg-primary-100 dark:hover:bg-primary-900/20 dark:active:bg-primary-900/30',
    'active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100',
    'transition-[background-color,transform] duration-fast ease-out',
    FOCUS_RING
  ),
  soft: cx(
    'inline-flex items-center justify-center gap-2 rounded-lg font-medium',
    'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300',
    'hover:bg-primary-100 active:bg-primary-200 dark:hover:bg-primary-900/30 dark:active:bg-primary-900/40',
    'active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100',
    'transition-[background-color,transform] duration-fast ease-out',
    FOCUS_RING
  ),
  link: cx(
    'inline-flex items-center justify-center gap-1 rounded font-medium',
    'bg-transparent text-primary-600 dark:text-primary-400 underline underline-offset-2',
    'hover:text-primary-700 dark:hover:text-primary-300',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    'transition-colors duration-fast ease-out',
    FOCUS_RING
  ),
};

// Utility-layer sizes win over the component-layer .btn-* defaults (no `!` needed).
const SIZES = {
  sm: 'min-h-[36px] px-3 py-1.5 text-sm',
  md: 'min-h-[44px] px-4 py-2 text-sm',
  lg: 'min-h-[52px] px-6 py-3 text-base',
};
const LINK_SIZES = {
  sm: 'min-h-[36px] px-1 text-sm',
  md: 'min-h-[44px] px-1 text-sm',
  lg: 'min-h-[52px] px-1 text-base',
};
const ICON_SIZE = { sm: 'w-4 h-4', md: 'w-5 h-5', lg: 'w-5 h-5' };

export default function Button({
  children,
  variant = 'primary',
  size = 'md',
  icon: Icon,
  iconRight: IconRight,
  loading = false,
  fullWidth = false,
  disabled,
  className = '',
  ...props
}) {
  const isLink = variant === 'link';
  const sizeCls = (isLink ? LINK_SIZES : SIZES)[size] || SIZES.md;
  const iconCls = ICON_SIZE[size] || ICON_SIZE.md;

  return (
    <button
      className={cx(
        VARIANTS[variant] || VARIANTS.primary,
        sizeCls,
        fullWidth && 'w-full',
        loading && 'relative',
        className
      )}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading && (
        <span className="absolute inset-0 flex items-center justify-center" aria-hidden="true">
          <span className={cx(iconCls, 'rounded-full border-2 border-current border-t-transparent animate-spin')} />
        </span>
      )}
      <span className={cx('inline-flex items-center justify-center gap-2', loading && 'invisible')}>
        {Icon && <Icon className={cx(iconCls, 'flex-shrink-0')} aria-hidden="true" />}
        {children}
        {IconRight && <IconRight className={cx(iconCls, 'flex-shrink-0')} aria-hidden="true" />}
      </span>
      {loading && <span className="sr-only">Loading</span>}
    </button>
  );
}
